'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
// Data für EmojiPicker wird dynamisch geladen – siehe unten
import { useSettings } from '@/providers/settings-provider';
import { BackendAuth } from '@/lib/auth-backend';
import { fetchUiState } from '@/lib/uiState';
import { toast } from 'sonner';

export function TelegramChatWidget() {
  const { getOption, storeOption } = useSettings();
  const enabled = getOption('features.telegramChat.enabled');
  const pollIntervalMs = getOption('features.telegramChat.pollIntervalMs') ?? 8000;

  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [chatTitle, setChatTitle] = useState('Support Chat');
  const pollTimerRef = useRef(null);
  const listRef = useRef(null);
  const atBottomRef = useRef(true);
  const replyToRef = useRef(null);
  const [replyTo, setReplyTo] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const inputElRef = useRef(null);
  const lastUpdateIdRef = useRef(null);
  const lastChangeTickRef = useRef(null);
  const lastReactionsTickRef = useRef(null);
  const streamOkRef = useRef(false);       // Live-Stream verbunden -> Abfrage nur noch Sicherheitsnetz
  const isOpenRef = useRef(false);
  const reloadRef = useRef(null);
  const lastLatestIdRef = useRef(null);
  const lastEditedRef = useRef(0);   // neueste gespeicherte Nachricht (DB, server-uebergreifend)
  // Lesestand dieses Benutzers beim Server speichern (Ungelesen-Zaehler je Benutzer)
  const markRead = async () => {
    try { await BackendAuth.apiCall('/api/telegram/read', { method: 'POST' }); } catch {}
  };
  const [available, setAvailable] = useState(null); // null=unknown, true=has backend config, false=no config
  // Verfügbarkeit via Aggregat-Endpoint prüfen (nur einblenden, wenn DB‑Config vorhanden)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const _r = await fetchUiState();
        const res = _r.status === 401 ? null : _r;
        if (!res) { // 401 - User logged out
          if (mounted) setAvailable(false);
          return;
        }
        const data = await res.json();
        if (!mounted) return;
        const ok = !!(data?.success && data?.data?.chat && data.data.chat.available === true);
        setAvailable(ok);
        if (ok) {
          const chTick = typeof data.data.chat.change_tick === 'number' ? data.data.chat.change_tick : null;
          const lu = typeof data.data.chat.last_update_id === 'number' ? data.data.chat.last_update_id : null;
          if (chTick != null) lastChangeTickRef.current = chTick;
          if (lu != null) lastUpdateIdRef.current = lu;
          const li = typeof data.data.chat.latest_id === 'number' ? data.data.chat.latest_id : null;
          if (li != null) lastLatestIdRef.current = li;
          // Ungelesen kommt vom Server, je Benutzer (nicht mehr im Browser hochgezaehlt)
          if (typeof data.data.chat.unread === 'number') setUnreadCount(Math.min(99, data.data.chat.unread));
        }
      } catch {
        if (mounted) setAvailable(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const [showEmoji, setShowEmoji] = useState(false);
  // Reaction picker state
  const [reactTarget, setReactTarget] = useState(null); // { mid, x, y }
  const pickerRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const takingShotRef = useRef(false);
  // Telegram-kompatible Reaction-Auswahl (ohne emoji-mart)
  const REACTS = useMemo(() => (
    ['👍','❤️','😂','😮','😢','🙏','🎉','🔥']
  ), []);
  // Eingabe-Emojis (kleines Grid)
  const EMOJIS = useMemo(() => (
    ['😀','😁','😂','🤣','😊','😍','😘','🤗','👍','👏','🙏','💪','🎉','🔥','✨','❤️','💙','💚','🙂','🙃','😅','😎','🤔','😴']
  ), []);
  const QUICK = useMemo(() => (
    ['😀','😂','😍','😊','👍','🎉','🔥']
  ), []);
  // emoji-mart nur für die Texteingabe (dynamisch, ohne Build-Fehler)
  const [emojiMartPicker, setEmojiMartPicker] = useState(null);
  const [emojiMartData, setEmojiMartData] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const dynImport = new Function('m', 'return import(m)');
        const [reactMod, dataMod] = await Promise.all([
          dynImport('@emoji-mart/react'),
          dynImport('@emoji-mart/data'),
        ]);
        if (!mounted) return;
        setEmojiMartPicker(() => (reactMod.default || reactMod.Picker || reactMod));
        setEmojiMartData(dataMod.default || dataMod);
      } catch {}
    })();
    return () => { mounted = false; };
  }, []);

  // no-op: Picker per dynamic import oben

  // Close picker on outside click
  useEffect(() => {
    if (!reactTarget) return;
    const onDoc = (e) => {
      const el = pickerRef.current;
      if (el && !el.contains(e.target)) setReactTarget(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [reactTarget]);

  // Close emoji-mart on outside click
  useEffect(() => {
    if (!showEmoji) return;
    const onDoc = (e) => {
      const el = emojiPickerRef.current;
      if (el && !el.contains(e.target)) setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showEmoji]);

  const submitReaction = async (mid, emoji) => {
    try {
      if (!mid || !emoji) return;
      await BackendAuth.apiCall('/api/telegram/react', {
        method: 'POST',
        body: JSON.stringify({ message_id: mid, emoji })
      });
      setMessages((prev) => prev.map((x) => x.message_id === mid ? { ...x, reaction: emoji } : x));
    } catch {}
  };

  // Poll Aggregat-UI-State; bei Tick-Änderung genau einmal Messages nachladen
  useEffect(() => {
    if (!enabled) return;
    if (available !== true) return;
    const pollState = async () => {
      try {
        const _r = await fetchUiState();
        const res = _r.status === 401 ? null : _r;
        if (!res) return; // 401 - User logged out, redirect already happened
        const data = await res.json();
        if (!data?.success || !data?.data?.chat) return;
        const chat = data.data.chat;
        const newLast = typeof chat.last_update_id === 'number' ? chat.last_update_id : null;
        const chTick = typeof chat.change_tick === 'number' ? chat.change_tick : null;

        if (newLast != null && lastUpdateIdRef.current == null) {
          lastUpdateIdRef.current = newLast;
        }
        const latestId = typeof chat.latest_id === 'number' ? chat.latest_id : null;
        if (!isOpen && typeof chat.unread === 'number') {
          setUnreadCount(Math.min(99, chat.unread));
        }
        const shouldReload = (
          isOpen && (
            (latestId != null && latestId !== lastLatestIdRef.current) ||
            (newLast != null && newLast > (lastUpdateIdRef.current ?? -1)) ||
            (chTick != null && chTick !== lastChangeTickRef.current)
          )
        );
        if (shouldReload) {
          try {
            const res2 = await BackendAuth.apiCall('/api/telegram/messages');
            if (!res2) return; // 401 - User logged out
            const data2 = await res2.json();
            if (data2?.success) {
              setMessages(data2.data?.messages || []);
              const dbg2 = data2?.data?.debug || {};
              const last2 = typeof dbg2.last_update_id === 'number' ? dbg2.last_update_id : newLast;
              lastUpdateIdRef.current = last2;
              if (latestId != null) lastLatestIdRef.current = latestId;
              if (chTick != null) lastChangeTickRef.current = chTick;
              if (atBottomRef.current || messages.length === 0) {
                setTimeout(() => scrollToBottom(), 150);
              }
              setUnreadCount(0);
              markRead();
            }
          } catch {}
        }
      } catch {}
    };

    pollState();
    // Mit Live-Stream reicht die Abfrage als Sicherheitsnetz (alle 60 s), sonst wie eingestellt
    pollTimerRef.current = setInterval(() => {
      if (streamOkRef.current && (Date.now() % 60000) > Math.max(3000, pollIntervalMs)) return;
      pollState();
    }, Math.max(3000, pollIntervalMs));
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [enabled, pollIntervalMs, isOpen, available]);

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // Live-Push per Server-Sent Events (01.10.2026): neue Nachricht / geaenderter Zaehler kommt sofort,
  // ohne auf die 8-s-Abfrage zu warten. Faellt der Stream aus, laeuft die Abfrage weiter.
  useEffect(() => {
    if (!enabled || available !== true) return undefined;
    let es = null; let closed = false; let retry = null;
    const connect = () => {
      if (closed) return;
      let tok = null;
      try { tok = localStorage.getItem('auth_token') || localStorage.getItem('token'); } catch {}
      if (!tok) { retry = setTimeout(connect, 5000); return; }
      es = new EventSource(`/api/telegram/stream?token=${encodeURIComponent(tok)}`);
      es.onopen = () => { streamOkRef.current = true; };
      es.addEventListener('chat', async (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (isOpenRef.current) {
            // Fenster offen: Nachrichten nachladen und gleich als gelesen melden
            if (d.latest_id !== lastLatestIdRef.current || (d.edited || 0) !== lastEditedRef.current) {
              lastLatestIdRef.current = d.latest_id;
              lastEditedRef.current = d.edited || 0;
              const r = await BackendAuth.apiCall('/api/telegram/messages');
              if (!r) return;
              const j = await r.json();
              if (j?.success) {
                setMessages(j.data?.messages || []);
                setUnreadCount(0);
                markRead();
                if (atBottomRef.current) setTimeout(() => scrollToBottom(), 150);
              }
            }
          } else {
            lastLatestIdRef.current = d.latest_id;
            setUnreadCount(Math.min(99, d.unread || 0));
          }
        } catch {}
      });
      es.onerror = () => {
        streamOkRef.current = false;
        try { es.close(); } catch {}
        if (!closed) retry = setTimeout(connect, 5000);
      };
    };
    connect();
    return () => { closed = true; streamOkRef.current = false; if (retry) clearTimeout(retry); try { es && es.close(); } catch {} };
  }, [enabled, available]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !isOpen || loadedOnce) return;
    if (available !== true) return;
    (async () => {
      try {
        setLoading(true);
        const res = await BackendAuth.apiCall('/api/telegram/messages');
        if (!res) { // 401 - User logged out
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (data?.success) {
          setMessages(data.data?.messages || []);
          setUnreadCount(0);
          markRead();
          const cfgName = data?.data?.config?.internal_name || data?.data?.config?.chat_name;
          if (cfgName) setChatTitle(cfgName);
          setLoadedOnce(true);
          setTimeout(() => scrollToBottom(), 250);
        }
      } catch {}
      finally { setLoading(false); }
    })();
  }, [enabled, isOpen, loadedOnce, available]);

  // Wenn keine Backend‑Config → gar nicht rendern (default: versteckt bis geprüft)
  if (available !== true) return null;

  if (!enabled) {
    return (
      <div style={styles.container}>
        <button
          type="button"
          onClick={() => storeOption('features.telegramChat.enabled', true)}
          title="Telegram Chat aktivieren"
          style={{ ...styles.fab, background: '#ffffff', color: 'var(--bs-primary)', border: '1px solid #e4e6ea' }}
        >
          <span style={{ fontSize: 18 }}>💬</span>
        </button>
      </div>
    );
  }

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text) return;
    // Capture and clear reply immediately
    const prevReply = replyToRef.current;
    const replyId = prevReply?.message_id ?? null;
    replyToRef.current = null;
    setReplyTo(null);
    // Optimistic insert
    const optimistic = {
      id: `tmp-${Date.now()}`,
      message_id: null,
      update_id: null,
      text,
      me: true,
      time: new Date().toISOString(),
      sender: '',
      reply_to_message_id: replyId,
    };
    setMessages((prev) => [...prev, optimistic]);
    const prevInput = inputValue;
    try {
      setInputValue('');
      try { if (inputElRef.current) inputElRef.current.style.height = ''; } catch {}
      if (inputElRef.current) inputElRef.current.style.height = '';
      const res = await BackendAuth.apiCall('/api/telegram/send', {
        method: 'POST',
        body: JSON.stringify({ text, reply_to_message_id: replyId }),
      });
      if (!res) { // 401 - User logged out
        // Revert optimistic
        setMessages((prev) => prev.filter((m) => m !== optimistic));
        setInputValue(prevInput);
        replyToRef.current = prevReply;
        setReplyTo(prevReply);
        return;
      }
      const data = await res.json();
      if (data?.success) {
        // Server may return fresh messages snapshot
        if (data.data?.messages) {
          setMessages(data.data.messages || []);
        } else {
          // fallback: reload
          const listRes = await BackendAuth.apiCall('/api/telegram/messages');
          if (!listRes) return; // 401 - User logged out
          const listData = await listRes.json();
          if (listData?.success) {
            setMessages(listData.data?.messages || []);
          }
        }
        queueMicrotask(() => scrollToBottom());
      } else {
        // revert optimistic
        setMessages((prev) => prev.filter((m) => m !== optimistic));
        setInputValue(prevInput);
        replyToRef.current = prevReply;
        setReplyTo(prevReply);
      }
    } catch {
      // revert optimistic
      setMessages((prev) => prev.filter((m) => m !== optimistic));
      setInputValue(prevInput);
      replyToRef.current = prevReply;
      setReplyTo(prevReply);
    }
  };

  const scrollToBottom = () => {
    try {
      const el = listRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    } catch {}
  };

  const handleScroll = (e) => {
    try {
      const el = e.currentTarget;
      const threshold = 16; // px
      atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    } catch {}
  };

  const scrollToMessageById = (mid) => {
    try {
      if (!mid) return;
      const container = listRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-message-id="${mid}"]`);
      if (el) {
        const headerOffset = 72;
        let top = Math.max(0, el.offsetTop - headerOffset);
        const bottom = el.offsetTop + el.offsetHeight;
        const viewBottom = top + container.clientHeight;
        if (bottom > viewBottom) {
          top = Math.max(0, bottom - container.clientHeight + 8);
        }
        container.scrollTo({ top, behavior: 'smooth' });
        setHighlightId(mid);
        setTimeout(() => setHighlightId(null), 1500);
      }
    } catch {}
  };

  return (
    <div style={{ ...styles.container, ...(replyTo ? styles.containerRaised : {}) }}>
      {!isOpen && (
        <button
          type="button"
          onClick={toggleOpen}
          title="Telegram Chat"
          style={styles.fab}
        >
          <span style={{ fontSize: 18 }}>💬</span>
          {unreadCount > 0 && (
            <span style={styles.badge}>{unreadCount}</span>
          )}
        </button>
      )}
      {isOpen && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <strong>{chatTitle}</strong>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={async () => {
                try {
                  const res = await BackendAuth.apiCall('/api/telegram/messages');
                  if (!res) return; // 401 - User logged out
                  const data = await res.json();
                  if (data?.success) {
                    setMessages(data.data?.messages || []);
                    setUnreadCount(0);
                    markRead();
                    const cfgName = data?.data?.config?.internal_name || data?.data?.config?.chat_name;
                    if (cfgName) setChatTitle(cfgName);
                  }
                } catch {}
              }} style={styles.refreshBtn} title="Aktualisieren">⟳</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={!!enabled}
                  onChange={(e) => storeOption('features.telegramChat.enabled', e.target.checked)}
                />
                aktiv
              </label>
              <button type="button" onClick={toggleOpen} style={styles.closeBtn}>×</button>
            </div>
          </div>
          <div style={styles.messages} ref={listRef} onScroll={handleScroll}>
            {loading ? (
              <div style={styles.loading}>Lade Nachrichten…</div>
            ) : null}
            {loadedOnce && !loading && messages.length === 0 ? (
              <div style={styles.empty}>Keine Nachrichten</div>
            ) : (
              messages.map((m, idx) => {
                const senderLabel = m.sender || (m.sender_username ? `@${m.sender_username}` : '');
                const replyTargetId = m.reply_to_message_id || null;
                const replied = replyTargetId ? messages.find(x => x.message_id === replyTargetId) : null;
                const hasReact = (Array.isArray(m.reactions) && m.reactions.length > 0) || (m.reactions_by_emoji && Object.keys(m.reactions_by_emoji).length > 0) || !!m.reaction;
                const iconEmoji = Array.isArray(m.reactions) && m.reactions.length > 0 ? m.reactions[0] : (m.reaction || '☺');
                return (
                  <div
                    key={idx}
                    data-message-id={m.message_id || ''}
                    style={{
                      ...styles.message,
                      ...(m.me ? styles.me : styles.other),
                      ...(highlightId && m.message_id === highlightId ? styles.highlight : {}),
                    }}
                  >
                    {senderLabel ? (
                      <div style={{ ...styles.sender, ...(m.me ? styles.senderMe : {}) }}>{senderLabel}</div>
                    ) : null}
                    {replyTargetId ? (
                      <div style={styles.replySnippet} onClick={() => scrollToMessageById(replyTargetId)} title={`Antwort auf #${replyTargetId}`}>
                        ↪︎ {replied ? `${String(replied.text || '').slice(0, 60)}${String(replied.text || '').length > 60 ? '…' : ''}` : `Antwort auf #${replyTargetId}`}
                      </div>
                    ) : null}
                    {/* Rich Media */}
                    {(() => {
                      const mt = m.media_type;
                      if (!mt) return null;
                      if (mt === 'photo') {
                        const src = m.message_id ? `/api/telegram/file?message_id=${m.message_id}` : '';
                        return (
                          <div style={{ marginBottom: 6 }}>
                            <img src={src} alt="Foto" style={{ maxWidth: 240, borderRadius: 6, display: 'block' }} />
                            {m.caption ? <div style={styles.text}>{linkify(m.caption)}</div> : null}
                          </div>
                        );
                      }
                      if (mt === 'document') {
                        const href = m.message_id ? `/api/telegram/file?message_id=${m.message_id}` : '#';
                        return (
                          <div style={{ marginBottom: 6 }}>
                            <a href={href} target="_blank" rel="noopener" style={{ fontSize: 13 }}>
                              📎 {m.file_name || 'Dokument'}{m.mime_type ? ` (${m.mime_type})` : ''}
                            </a>
                          </div>
                        );
                      }
                      if (mt === 'video') {
                        const href = m.message_id ? `/api/telegram/file?message_id=${m.message_id}` : '';
                        return (
                          <div style={{ marginBottom: 6 }}>
                            <video src={href} controls style={{ maxWidth: 240, borderRadius: 6 }} />
                            {m.caption ? <div style={styles.text}>{linkify(m.caption)}</div> : null}
                          </div>
                        );
                      }
                      if (mt === 'voice' || mt === 'audio') {
                        const href = m.message_id ? `/api/telegram/file?message_id=${m.message_id}` : '';
                        return (
                          <div style={{ marginBottom: 6 }}>
                            <audio src={href} controls />
                            {m.caption ? <div style={styles.text}>{linkify(m.caption)}</div> : null}
                          </div>
                        );
                      }
                      if (mt === 'sticker') {
                        return <div style={styles.text}>Sticker {m.sticker_emoji ? ` ${m.sticker_emoji}` : ''}</div>;
                      }
                      if (mt === 'location') {
                        const lat = m.location_lat; const lon = m.location_lon;
                        const href = (lat && lon) ? `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lon)}` : '#';
                        return (
                          <div style={{ marginBottom: 6 }}>
                            <a href={href} target="_blank" rel="noopener" style={{ fontSize: 13 }}>📍 Standort öffnen</a>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* Text */}
                    {!(
                      (m.media_type === 'location' && (m.text === '[Location]' || !m.text)) ||
                      (m.media_type === 'photo' && (m.text === '[Photo]' || !m.text))
                    ) && (
                      <div style={styles.text}>{linkify(m.text)}{m.edited && <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 6 }}>(bearbeitet)</span>}</div>
                    )}
                    {m.reactions_by_emoji && Object.keys(m.reactions_by_emoji).length > 0 ? (
                      <div style={styles.reactionsRow}>
                        {Object.entries(m.reactions_by_emoji).map(([emo, users], i) => (
                          <span key={i} style={styles.reactionChip} title={Array.isArray(users) ? users.join(', ') : ''}>{emo} {Array.isArray(users) ? users.length : ''}</span>
                        ))}
                      </div>
                    ) : (m.reactions_counts && Object.keys(m.reactions_counts).length > 0 ? (
                      <div style={styles.reactionsRow}>
                        {Object.entries(m.reactions_counts).map(([emo, count], i) => (
                          <span key={i} style={styles.reactionChip}>{emo} {Number(count) || ''}</span>
                        ))}
                      </div>
                    ) : (Array.isArray(m.reactions) && m.reactions.length > 0 ? (
                      <div style={styles.reactionsRow}>
                        {m.reactions.map((r, i) => (
                          <span key={i} style={styles.reactionChip}>{r}</span>
                        ))}
                      </div>
                    ) : null))}
                    <div style={styles.metaRow}>
                      <button type="button" style={styles.replyIcon} title="Antworten"
                        onClick={() => { if (m.message_id) { replyToRef.current = m; setReplyTo(m); setInputValue(v => v); setTimeout(() => { if (inputElRef.current) inputElRef.current.focus(); }, 0); } }}>
                        ↩
                      </button>
                      <div style={styles.metaRight}>
                        <button type="button" style={styles.replyIcon} title="Löschen"
                          onClick={async () => {
                            if (typeof window !== 'undefined') {
                              const ok = window.confirm('Nachricht wirklich löschen? (wird auch im Telegram gelöscht)');
                              if (!ok) return;
                            }
                            try {
                              if (!m.message_id) return;
                              const res = await BackendAuth.apiCall('/api/telegram/delete', { method: 'POST', body: JSON.stringify({ message_id: m.message_id }) });
                              if (!res) return;
                              const data = await res.json();
                              const d = data?.data || {};
                              if (data?.success && d.deleted) {
                                if (Array.isArray(d.messages)) setMessages(d.messages);
                                else setMessages((prev) => prev.filter((x) => x.message_id !== m.message_id));
                                if (d.telegram_reason === 'not_deletable') {
                                  toast.info('Im Chat gelöscht. In Telegram selbst ist das nicht möglich (älter als 48 Stunden).');
                                }
                              } else {
                                toast.error('Löschen fehlgeschlagen – Telegram ist gerade nicht erreichbar. Bitte später erneut versuchen.');
                                if (Array.isArray(d.messages)) setMessages(d.messages);
                              }
                            } catch {
                              toast.error('Löschen fehlgeschlagen.');
                            }
                          }}>🗑️</button>
                        <div style={styles.time}>{formatRelativeTime(m.time)}</div>
                        <button
                          type="button"
                          title={hasReact ? 'Reaktion entfernen' : 'Reagieren'}
                          style={{
                            ...styles.reactIcon,
                            ...(hasReact ? styles.reactIconActive : {}),
                          }}
                          onClick={async (e) => {
                            if (!m.message_id) return;
                            if (hasReact) {
                              try {
                                await BackendAuth.apiCall('/api/telegram/react', {
                                  method: 'POST',
                                  body: JSON.stringify({ message_id: m.message_id, clear: true })
                                });
                                setMessages((prev) => prev.map((x) => x.message_id === m.message_id ? { ...x, reactions: [], reaction: null } : x));
                              } catch {}
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setReactTarget({ mid: m.message_id, x: rect.right, y: rect.bottom });
                          }}
                        >
                          <span style={{ opacity: hasReact ? 1 : 0.45 }}>{iconEmoji}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div style={styles.inputRow}>
            {reactTarget ? (
              <div
                ref={pickerRef}
                style={{
                  position: 'fixed',
                  left: Math.max(8, reactTarget.x - 220),
                  top: reactTarget.y + 8,
                  zIndex: 2000,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                  padding: 6,
                  display: 'flex',
                  gap: 6,
                }}
              >
                {REACTS.map((emo) => (
                  <button key={emo} type="button" style={styles.reactChoice}
                    onClick={() => { if (reactTarget?.mid) submitReaction(reactTarget.mid, emo); setReactTarget(null); }}
                    title={`Reagieren ${emo}`}
                  >{emo}</button>
                ))}
              </div>
            ) : null}
            <div style={styles.inputWrap}>
            {replyTo ? (
              <div style={styles.replyBanner}>
                <span style={styles.replyBannerText}>↩ Antwort auf #{replyTo?.message_id}: {String(replyTo?.text || '').slice(0, 120)}{String(replyTo?.text || '').length > 120 ? '…' : ''}</span>
                <button type="button" style={styles.replyClose}
                  onClick={() => { replyToRef.current = null; setReplyTo(null); setInputValue(v => v); queueMicrotask(() => { if (inputElRef.current) inputElRef.current.focus(); }); }}
                  title="Antwort schließen">×</button>
                <button type="button" style={styles.replyJump}
                  onClick={() => { if (replyTo?.message_id) scrollToMessageById(replyTo.message_id); }}
                  title="Zur Nachricht springen">⤵︎</button>
              </div>
            ) : null}
              <textarea
                ref={inputElRef}
                style={styles.textarea}
                placeholder="Nachricht schreiben... (Shift+Enter für Zeilenumbruch)"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 220) + 'px'; }}
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              />
              
            </div>
            <button type="button" style={styles.sendBtn} title="Senden" onClick={handleSend}>➤</button>
          </div>
          <div style={styles.controlsRow}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
              {QUICK.map((q) => (
                <button key={q} type="button" style={styles.emojiItem} onClick={() => {
                  try {
                    const el = inputElRef.current;
                    const start = el.selectionStart ?? inputValue.length;
                    const end = el.selectionEnd ?? inputValue.length;
                    const newVal = inputValue.slice(0, start) + q + inputValue.slice(end);
                    setInputValue(newVal);
                    queueMicrotask(() => { el?.focus(); const pos = start + q.length; el?.setSelectionRange?.(pos, pos); });
                  } catch { setInputValue((v) => v + q); }
                }}>{q}</button>
              ))}
            </div>
            <button type="button" style={styles.emojiBtn} title="Emoji einfügen" onClick={() => setShowEmoji((v) => !v)}>
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span>😊</span>
                <span style={styles.emojiBtnBadge}>+</span>
              </span>
            </button>
            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={async (e) => {
              try {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('file', f);
                if (replyToRef.current?.message_id) fd.append('reply_to_message_id', String(replyToRef.current.message_id));
                const res = await BackendAuth.apiCall('/api/telegram/send_file', { method: 'POST', body: fd });
                if (!res) return;
                const data = await res.json();
                if (data?.success && data.data?.messages) {
                  setMessages(data.data.messages);
                  setTimeout(() => scrollToBottom(), 100);
                }
              } catch {}
              finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
            }} />
            <button type="button" style={styles.emojiBtn} title="Datei senden" onClick={() => fileInputRef.current?.click?.()}>📎</button>
            <button type="button" style={styles.emojiBtn} title="Screenshot aufnehmen" onClick={async () => {
              if (takingShotRef.current) return;
              try {
                takingShotRef.current = true;
                if (!navigator.mediaDevices?.getDisplayMedia) return;
                const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
                const [track] = stream.getVideoTracks();
                const finish = () => { try { track?.stop(); stream.getTracks().forEach(t => t.stop()); } catch {} };
                try {
                  let bitmap = null;
                  if (typeof window.ImageCapture === 'function') {
                    try { const cap = new window.ImageCapture(track); bitmap = await cap.grabFrame(); } catch {}
                  }
                  const canvas = document.createElement('canvas');
                  if (bitmap) {
                    canvas.width = bitmap.width; canvas.height = bitmap.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(bitmap, 0, 0);
                  } else {
                    const video = document.createElement('video');
                    video.srcObject = stream; video.muted = true; await video.play();
                    await new Promise(r => setTimeout(r, 150));
                    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0);
                  }
                  const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
                  if (!blob) return;
                  const fd = new FormData();
                  fd.append('file', new File([blob], 'screenshot.png', { type: 'image/png' }));
                  if (replyToRef.current?.message_id) fd.append('reply_to_message_id', String(replyToRef.current.message_id));
                  const res = await BackendAuth.apiCall('/api/telegram/send_file', { method: 'POST', body: fd });
                  if (!res) return;
                  const data = await res.json();
                  if (data?.success && data.data?.messages) {
                    setMessages(data.data.messages);
                    setTimeout(() => scrollToBottom(), 100);
                  }
                } finally {
                  finish();
                }
              } catch {} finally { takingShotRef.current = false; }
            }}>📸</button>

            {showEmoji && emojiMartPicker && emojiMartData ? (
              <div ref={emojiPickerRef} style={{ position: 'absolute', bottom: 56, left: 8, zIndex: 2000 }}>
                {(() => {
                  const Picker = emojiMartPicker;
                  return (
                    <Picker
                      data={emojiMartData}
                      onEmojiSelect={(e) => {
                        const emo = e?.native || e?.shortcodes || e?.id || '';
                        if (!emo) return;
                        try {
                          const el = inputElRef.current;
                          const start = el.selectionStart ?? inputValue.length;
                          const end = el.selectionEnd ?? inputValue.length;
                          const newVal = inputValue.slice(0, start) + emo + inputValue.slice(end);
                          setInputValue(newVal);
                          setShowEmoji(false);
                          queueMicrotask(() => { el?.focus(); const pos = start + emo.length; el?.setSelectionRange?.(pos, pos); });
                        } catch { setInputValue((v) => v + emo); setShowEmoji(false); }
                      }}
                      theme="light"
                      previewPosition="none"
                      skinTonePosition="none"
                      searchPosition="none"
                    />
                  );
                })()}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// URLs im Nachrichtentext anklickbar machen (http(s):// und www.) – als React-Elemente, kein innerHTML
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
function linkify(value) {
  if (value == null) return null;
  const str = String(value);
  if (!str) return str;
  const parts = str.split(URL_RE);
  if (parts.length === 1) return str;
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    // Satzzeichen am Ende gehoeren nicht zum Link ("... siehe https://x.at/abc.")
    const m = part.match(/^(.*?)([.,;:!?)\]]*)$/);
    const url = m ? m[1] : part;
    const tail = m ? m[2] : '';
    if (!url) return part;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return (
      <span key={i}>
        <a href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>{url}</a>{tail}
      </span>
    );
  });
}

function formatRelativeTime(isoOrStr) {
  try {
    if (!isoOrStr) return '';
    const dt = new Date(isoOrStr);
    if (isNaN(dt.getTime())) return String(isoOrStr);
    const now = new Date();
    const diffMs = now - dt;
    const sec = Math.floor(diffMs / 1000);
    // Uhr des PCs geht gegenueber dem Server leicht vor/nach -> nie negative Sekunden anzeigen
    if (sec < 10) return 'gerade eben';
    if (sec < 60) return `vor ${sec} Sek.`;
    const min = sec / 60;
    if (min < 60) return `vor ${Math.round(min)} Min.`;
    const hrs = min / 60;
    if (hrs < 24) {
      const whole = Math.floor(hrs);
      const frac = Math.round((hrs - whole) * 10) / 10; // eine Nachkommastelle
      return frac === 0 ? `vor ${whole} Std.` : `vor ${(whole + frac).toFixed(1)} Std.`;
    }
    // gestern
    const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (
      dt.getFullYear() === y.getFullYear() &&
      dt.getMonth() === y.getMonth() &&
      dt.getDate() === y.getDate()
    ) {
      return `gestern ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    // fallback: dd.mm.yyyy, HH:MM
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    const time = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dd}.${mm}.${yyyy}, ${time}`;
  } catch {
    return String(isoOrStr || '');
  }
}

const styles = {
  container: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    zIndex: 1200,
  },
  containerRaised: {
    bottom: 96,
  },
  fab: {
    position: 'relative',
    width: 56,
    height: 56,
    borderRadius: 28,
    border: 'none',
    background: 'var(--bs-primary) ',
    color: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    cursor: 'pointer',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    background: '#f1416c',
    borderRadius: 10,
    color: '#fff',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
  },
  panel: {
    width: 380,
    height: 560,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHeader: {
    height: 48,
    background: '#fafafa',
    borderBottom: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    lineHeight: '26px',
    textAlign: 'center',
  },
  messages: {
    flex: 1,
    padding: 12,
    overflowY: 'auto',
    overflowX: 'hidden',
    background: '#f8f9fa',
  },
  loading: {
    fontSize: 13,
    color: '#666',
    marginBottom: 8,
  },
  empty: {
    fontSize: 13,
    color: '#666',
  },
  message: {
    maxWidth: '80%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    padding: '8px 10px',
    borderRadius: 8,
    marginBottom: 8,
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  me: {
    marginLeft: 'auto',
    background: '#e9f6ff',
  },
  other: {
    marginRight: 'auto',
    background: '#fff',
  },
  text: {
    fontSize: 14,
    color: '#111',
    // Zeilenumbrueche aus der Nachricht behalten + lange Woerter/Links umbrechen (06.10.2026)
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  link: {
    color: 'var(--bs-primary)',
    textDecoration: 'underline',
    overflowWrap: 'anywhere',
    wordBreak: 'break-all',
  },
  sender: {
    fontSize: 12,
    color: '#555',
    marginBottom: 2,
    fontWeight: 600,
  },
  senderMe: {
    color: '#0b6fb8',
  },
  time: {
    fontSize: 11,
    color: '#777',
    marginTop: 4,
    textAlign: 'right',
  },
  reactionBadge: {
    marginTop: 4,
    fontSize: 16,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
  },
  metaRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  reactionsRow: {
    display: 'flex',
    gap: 6,
    marginTop: 4,
  },
  reactionChip: {
    fontSize: 14,
  },
  replyIcon: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    fontSize: 14,
    color: '#555',
  },
  reactIcon: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    fontSize: 14,
    color: '#aaa',
    transition: 'color 0.15s ease',
  },
  reactIconActive: {
    color: '#f59e0b',
  },
  inputRow: {
    display: 'flex',
    gap: 8,
    padding: 10,
    borderTop: '1px solid #eee',
    background: '#fff',
    position: 'relative',
    flexWrap: 'nowrap',
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    height: 36,
    borderRadius: 8,
    border: '1px solid #ddd',
    padding: '0 10px',
    outline: 'none',
  },
  inputWrap: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  textarea: {
    width: '100%',
    minHeight: 64,
    maxHeight: 220,
    resize: 'vertical',
    borderRadius: 8,
    border: '1px solid #ddd',
    padding: '8px 10px',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: 15,
    lineHeight: '20px',
    overflow: 'auto',
    background: '#fff',
    color: '#111',
    colorScheme: 'light',
    boxSizing: 'border-box',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    overflow: 'visible',
    paddingTop: 2,
    position: 'relative',
    width: '100%',
  },
  emojiBtn: {
    border: '1px solid #ddd',
    background: '#fff',
    borderRadius: 6,
    width: 28,
    height: 28,
    cursor: 'pointer',
    position: 'relative',
  },
  emojiBtnBadge: {
    position: 'absolute',
    right: -8,
    top: -10,
    background: '#f1416c',
    color: '#fff',
    fontSize: 10,
    lineHeight: '12px',
    width: 16,
    height: 16,
    borderRadius: 8,
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    border: '1px solid #fff',
    zIndex: 5,
  },
  emojiPanel: {
    position: 'absolute',
    bottom: 48,
    left: 0,
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
    padding: 6,
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: 4,
    zIndex: 5,
    width: 240,
  },
  emojiItem: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 18,
  },
  sendBtn: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 16,
    border: 'none',
    background: 'var(--bs-primary)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  replyBanner: {
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: 0,
    fontSize: 12,
    color: '#555',
    background: '#eef6ff',
    border: '1px solid #cfe7ff',
    borderRadius: 6,
    padding: '6px 28px 6px 10px',
  },
  replyClose: {
    position: 'absolute',
    right: 8,
    top: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#f1416c',
    fontSize: 14,
  },
  replyJump: {
    marginLeft: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
  replySnippet: {
    fontSize: 12,
    color: '#666',
    background: '#f1f3f5',
    borderLeft: '3px solid #cbd5e1',
    padding: '2px 6px',
    borderRadius: 4,
    marginBottom: 4,
    cursor: 'pointer',
  },
  highlight: {
    outline: '2px solid #009ef7',
    transition: 'outline 0.2s ease-in-out',
  },
  reactRow: {
    display: 'flex',
    gap: 6,
    marginTop: 4,
  },
  reactBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
    opacity: 0.7,
  },
  reactChoice: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 20,
    lineHeight: '24px',
  },
};


